import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { faqs } from '../data/faqData';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: 'website' | 'article';
  noIndex?: boolean;
  url?: string;
  faqItems?: { question: string; answer: string }[];
}

const SEO: React.FC<SEOProps> = ({
  title = 'Arabisch lernen online | Live-Unterricht für jedes Niveau',
  description = 'Arabisch lernen mit echtem Fortschritt – strukturierte Live-Kurse online auf Deutsch. Vom Alphabet bis zum Quran-Verständnis. Kleine Gruppen, persönliche Betreuung. Jetzt anmelden.',
  keywords = 'Arabisch lernen, Arabisch online lernen, Arabischkurs online, Arabisch Kurs, Hocharabisch lernen, Arabisch für Anfänger, Arabisch lernen Deutsch, Arabischunterricht online, Quran Arabisch lernen, Arabisch live Unterricht, Madina Buch, Arabya Bayna Yadayk, Arabisch lesen lernen, Arabisch Hamburg',
  image = '/logo.png',
  type = 'website',
  noIndex = false,
  url,
  faqItems,
}) => {
  const location = useLocation();
  const baseUrl = 'https://arabicroots.de';

  const normalizedPath = url
    ? url.startsWith('http')
      ? url
      : `${baseUrl}${url.startsWith('/') ? url : `/${url}`}`
    : `${baseUrl}${location.pathname}`;

  const fullTitle = title.toLowerCase().includes('arabicroots')
    ? title
    : `${title} | Arabicroots`;

  const fullImage = image.startsWith('http') ? image : `${baseUrl}${image}`;

  const robots = noIndex ? 'noindex, nofollow' : 'index, follow';

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': ['EducationalOrganization', 'LocalBusiness'],
    name: 'Arabicroots',
    url: baseUrl,
    logo: `${baseUrl}/logo.png`,
    image: fullImage,
    description,
    email: 'info@arabicroots.de',
    telephone: '+49 178 8378689',
    priceRange: '€€',
    currenciesAccepted: 'EUR',
    paymentAccepted: 'Cash, Credit Card, Bank Transfer',
    areaServed: { '@type': 'Country', name: 'Germany' },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '5',
      bestRating: '5',
      worstRating: '1',
      ratingCount: '6',
      reviewCount: '6',
    },
    sameAs: [
      'https://instagram.com/arabic.roots',
      'https://youtube.com/@arabicroots',
      'https://tiktok.com/@arabicroots',
    ],
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Ludwig-Erhard-Str. 18',
      postalCode: '20459',
      addressLocality: 'Hamburg',
      addressRegion: 'Hamburg',
      addressCountry: 'DE',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: '53.5511',
      longitude: '9.9937',
    },
  };

  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Arabicroots',
    url: baseUrl,
    inLanguage: 'de-DE',
  };

  const webpageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: fullTitle,
    url: normalizedPath,
    description,
    inLanguage: 'de-DE',
    isPartOf: {
      '@type': 'WebSite',
      name: 'Arabicroots',
      url: baseUrl,
    },
  };

  const isHomePage = location.pathname === '/';
  const isRegistrationPage = location.pathname.includes('/anmelden');

  const courseSchemas = (isHomePage || isRegistrationPage)
    ? [
        { level: 'Anfänger', name: 'Buchstabenkurs – Arabisch lesen und schreiben lernen', description: 'Lerne das arabische Alphabet von Grund auf – alle Buchstaben lesen, schreiben und korrekt aussprechen. Inkl. grundlegender Tajweed-Regeln.', educationalLevel: 'Beginner', duration: 'PT20W' },
        { level: 'Grundstufe', name: 'Arabisch Grundlagenkurs (Madina Buch 1)', description: 'Einstieg in Arabisch nach dem Buchstabenkurs: Grammatikgrundlagen, Grundwortschatz und einfache Gespräche auf Basis von Madina Buch 1.', educationalLevel: 'Beginner', duration: 'PT35W' },
        { level: 'Aufbaustufe', name: 'Arabisch Aufbaukurs (Madina Buch 2)', description: 'Vertiefung der Arabischkenntnisse: weiterführende Grammatik, erweiterter Wortschatz und anspruchsvollere Texte (Madina Buch 2).', educationalLevel: 'Intermediate', duration: 'PT35W' },
        { level: 'Fortgeschritten', name: 'Arabisch Fortgeschrittenenkurs (Madina Buch 3)', description: 'Für Lernende mit solider Grundlage: fortgeschrittene Grammatik und sicheres Textverständnis (Madina Buch 3).', educationalLevel: 'Advanced', duration: 'PT35W' },
        { level: 'Experte', name: 'Arabisch Expertenkurs (Al-Ajrumiyyah)', description: 'Tiefes Arabischverständnis: systematische Grammatik nach Al-Ajrumiyyah und Arabya Bayna Yadayk 4.', educationalLevel: 'Advanced', duration: 'PT35W' },
        { level: 'Meisterstufe', name: 'Arabisch Meisterstufe – Originaltexte lesen', description: 'Selbstständiges Lesen und Verstehen arabischer Originaltexte für Fortgeschrittene.', educationalLevel: 'Expert', duration: 'PT50W' },
      ].map(course => ({
        '@context': 'https://schema.org',
        '@type': 'Course',
        name: course.name,
        description: course.description,
        provider: {
          '@type': 'EducationalOrganization',
          name: 'Arabicroots',
          url: baseUrl,
        },
        courseMode: 'online',
        inLanguage: 'de',
        educationalLevel: course.level,
        teaches: 'Arabisch (Hocharabisch / Fusha)',
        hasCourseInstance: {
          '@type': 'CourseInstance',
          courseMode: 'online',
          courseWorkload: course.duration,
        },
      }))
    : [];

  const faqSource = faqItems ?? (isHomePage ? faqs : null);

  const faqSchema = faqSource
    ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqSource.map(faq => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      }
    : null;

  return (
    <Helmet>
      {/* Basic */}
      <html lang="de" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="Arabicroots" />
      <meta name="robots" content={robots} />
      <meta name="theme-color" content="#970000" />
      <link rel="canonical" href={normalizedPath} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={normalizedPath} />
      <meta property="og:image" content={fullImage} />
      <meta property="og:site_name" content="Arabicroots" />
      <meta property="og:locale" content="de_DE" />
      <meta property="og:image:alt" content="Arabicroots – Arabisch lernen online" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={fullImage} />
      <meta name="twitter:site" content="@arabic.roots" />
      <meta name="twitter:creator" content="@arabic.roots" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>

      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>

      <script type="application/ld+json">
        {JSON.stringify(webpageSchema)}
      </script>

      {courseSchemas.map((schema, i) => (
        <script key={`course-${i}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
